import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { Input } from '../../../../components/ui/input';
import { firebaseAuth } from '../../../../lib/firebase';
import {
  effectiveCategory,
  filterLibrary,
  groupMonthly,
  LIBRARY_SECTION_COPY,
  libraryCategories,
  noMatchesSentence,
  periodLabel,
} from '../../../../lib/portalLibrary/listShapes';
import type {
  PortalLibraryClientCategory,
  PortalLibrarySection,
  PortalLibraryTile,
} from '../../../../types/portalLibrary';
import { fetchPortalLibrary } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { LibraryTile } from './LibraryTile';

// CLIENT PORTAL LIBRARY — the three list pages (brief §7).
//
// ⚠️⚠️ TWO LIST SHAPES, chosen by the SECTION, not one component with a flag
// threaded through it: Monthly campaigns is a time series (newest first, grouped
// by year); Alternate campaigns and Kyck college are evergreen (one flat grid,
// filtered by tag chips, with search). The shapes live in
// src/lib/portalLibrary/listShapes.ts.
//
// ⚠️ EMPTY IS CONTENT: every section has its own sentence saying what it is and
// why it is empty, and "a search found nothing" is a different sentence again.
//
// ⚠️ Nothing on this page decides who sees what. The server resolves the scope
// from the session and applies the audience; this page renders what it is given.

export function LibraryPage({ section }: { section: PortalLibrarySection }) {
  const copy = LIBRARY_SECTION_COPY[section];
  const query = useQuery({
    queryKey: ['portal-library-tiles', section],
    queryFn: () => fetchPortalLibrary(section),
    retry: false,
  });

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">{copy.title}</h1>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">{copy.intro}</p>

      <div className="mt-6">
        {query.isPending ? (
          <CenteredSpinner />
        ) : query.isError ? (
          <div className="max-w-md">
            <p className="text-sm text-muted-foreground">
              We couldn’t load this section. Try again in a moment.
            </p>
            <Button variant="outline" className="mt-3" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : query.data.tiles.length === 0 ? (
          <p className="max-w-prose text-sm text-muted-foreground" data-testid="library-empty">
            {copy.empty}
          </p>
        ) : section === 'strategy.monthly' ? (
          <MonthlyList tiles={query.data.tiles} />
        ) : (
          <EvergreenList tiles={query.data.tiles} categories={query.data.categories} />
        )}
      </div>
    </PortalShell>
  );
}

/** The time series: newest first, a heading per year. */
export function MonthlyList({ tiles }: { tiles: PortalLibraryTile[] }) {
  const groups = groupMonthly(tiles);
  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <section key={g.year} aria-labelledby={`plib-year-${g.year}`}>
          <h2 id={`plib-year-${g.year}`} className="plib-group-heading">
            {g.year}
          </h2>
          <ul className="plib-grid">
            {g.tiles.map((t) => (
              <li key={t.id}>
                <LibraryTile tile={t} />
                <span className="sr-only">{periodLabel(t.period)}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The evergreen shape: search over ONE flat grid, plus — for Kyck college —
 * CATEGORY chips in their configured order (Round 4 §6). ⚠️ Tags are searched
 * and NEVER rendered. The chips are a FILTER, not headings (see listShapes.ts).
 */
export function EvergreenList({
  tiles,
  categories = [],
}: {
  tiles: PortalLibraryTile[];
  categories?: PortalLibraryClientCategory[];
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const chips = useMemo(() => libraryCategories(categories, tiles), [categories, tiles]);
  const categoryId = effectiveCategory(selected, chips);
  const categoryName = chips.find((c) => c.id === categoryId)?.name ?? null;
  const shown = useMemo(
    () => filterLibrary(tiles, search, categoryId),
    [tiles, search, categoryId],
  );
  return (
    <div className="space-y-6">
      <div className="max-w-sm">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          aria-label="Search this section"
        />
      </div>
      {chips.length > 0 && (
        <div className="plib-chips" role="group" aria-label="Filter by category">
          <button
            type="button"
            className="plib-chip"
            aria-pressed={categoryId === null}
            onClick={() => setSelected(null)}
          >
            All
          </button>
          {chips.map((c) => (
            <button
              key={c.id}
              type="button"
              className="plib-chip"
              aria-pressed={categoryId === c.id}
              onClick={() => setSelected(categoryId === c.id ? null : c.id)}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="library-no-matches">
          {noMatchesSentence(search, categoryName)}
        </p>
      ) : (
        <ul className="plib-grid">
          {shown.map((t) => (
            <li key={t.id}>
              <LibraryTile tile={t} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
